import { ChangeDetectorRef, Component, inject, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterModule, RouterOutlet } from '@angular/router';
import { AuthService } from './services/auth.service';
import { PrimeNGConfig } from 'primeng/api';
import { ImageLoader } from './utils/image-loader';
import { ImageOptimizationService } from './services/image-optimization.service';
import { NavComponent } from './shared/nav/nav.component';
import { ToastComponent } from './shared/toast/toast.component';
import { CommonModule } from '@angular/common';
import { NgxSpinnerModule } from 'ngx-spinner';
import { GenericDialogComponent } from './shared/generic-dialog/generic-dialog.component';
import { DownloadComponent } from './shared/download/download.component';
import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';
import { LOCALE_ID } from '@angular/core';
import { ClientesModule } from './modules/clientes/clientes.module';
import { AuthModule } from './auth/auth.module';
import { ExtratoBancarioModule } from './modules/extrato-bancario/extrato-bancario.module';
import { PlanoDeContasModule } from './modules/plano-de-contas/plano-de-contas.module';
import { EmpresaModule } from './modules/empresa/empresa.module';
import { NfseEmissaoModule } from './modules/nfse/emissao/nfse-emissao.module';
import { FieldsetModule } from 'primeng/fieldset';
import { FolhaPagamentosModule } from './modules/folha-pagamentos/folha-pagamentos.module';
import { ReceitasBrutasAnterioresModule } from './modules/receitas-brutas-anteriores/receitas-brutas-anteriores.module';
import { ListarLancamentoCaixaComponent } from './modules/lancamento-caixa/listar-lancamento-caixa/listar-lancamento-caixa.component';
import { LancamentoCaixaModule } from './modules/lancamento-caixa/lancamento-caixa.module';
import { DeclaracoesFiscaisModule } from './modules/declaracoes-fiscais/declaracoes-fiscais.module';
import { FaturamentoModule } from './modules/faturamento/faturamento.module';
import { BalanceteModule } from './modules/balancete/balancete.module';
import { BalancoPatrimonialModule } from './modules/balanco-patrimonial/balanco-patrimonial.module';
import { DreModule } from './modules/dre/dre.module';
import { RazaoModule } from './modules/razao/razao.module';
import { SociosModule } from './modules/socios/socios.module';
import { DividendosModule } from './modules/dividendos/dividendos.module';
import { ScrollTopModule } from 'primeng/scrolltop';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { TooltipModule } from 'primeng/tooltip';
import { TributosModule } from './modules/tributos/tributos.module';
import { FileSaverModule } from 'ngx-filesaver';
import { ImportacaoNfseEmitidasModule } from './modules/nfse/Importacao-nfse-emitidas/importacao-nfse-emitidas.module';
import { Subject, takeUntil } from 'rxjs';

registerLocaleData(localePt);

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    AuthModule,
    ExtratoBancarioModule,
    NfseEmissaoModule,
    NavComponent,
    ToastComponent,
    CommonModule,
    NgxSpinnerModule,
    ClientesModule,
    FileSaverModule,
    FieldsetModule,
    PlanoDeContasModule,
    TributosModule,
    FolhaPagamentosModule,
    ReceitasBrutasAnterioresModule,
    DeclaracoesFiscaisModule,
    FaturamentoModule,
    BalanceteModule,
    BalancoPatrimonialModule,
    DreModule,
    EmpresaModule,
    RazaoModule,
    LancamentoCaixaModule,
    SociosModule,
    DividendosModule,
    ScrollTopModule,
    DialogModule,
    ButtonModule,
    ChipModule,
    TooltipModule,
    ImportacaoNfseEmitidasModule
  ],
  templateUrl: './app.component.html',
  providers: [{ provide: LOCALE_ID, useValue: 'pt' }],
  styleUrls: ['./app.component.scss'],
})

export class AppComponent implements OnInit, OnDestroy {
  titulo: string = '';
  icone: string = '';
  tituloDescricao: string = '';
  textoDescricao: string = '';
  mostrarDescricao: boolean = false;

  authService = inject(AuthService);
  primengConfig = inject(PrimeNGConfig);
  cdr = inject(ChangeDetectorRef);
  router = inject(Router);
  visible: boolean = false;

  isForgotPasswordClicked: boolean = false;
  isLoggedIn: boolean = false;
  title = 'adv-contabil-front';
  private noFieldsetRoutes = [
    'sign-up-email',
    'verify-email',
    'forgot-password',
    'login',
    'cadastro',
    'reset-password',
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private imageOptimizationService: ImageOptimizationService
  ) { }

  ngOnInit() {
    // Configurar PrimeNG
    this.primengConfig.ripple = true;
    this.primengConfig.zIndex = {
      modal: 1100,
      overlay: 1000,
      menu: 1000,
      tooltip: 1100
    };

    // Otimizar carregamento de imagens
    this.imageOptimizationService.preloadCriticalImages();
    
    // Aplicar lazy loading após o DOM estar pronto
    setTimeout(() => {
      this.imageOptimizationService.applyLazyLoading();
    }, 100);

    // Configurar observador de autenticação
    this.authService.authStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.isLoggedIn = status;
        this.cdr.detectChanges();
      });

    // Configurar observador de navegação
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Limpar cache de imagens periodicamente
        if (Math.random() < 0.1) { // 10% de chance a cada navegação
          this.imageOptimizationService.clearCache();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onTituloChange(novoTitulo: string) {
    this.titulo = novoTitulo;
  }

  onIconeChange(novoIcone: string) {
    this.icone = novoIcone;
  }

  onActivate(component: any) {
    if (component.tituloChange) {
      component.tituloChange
        .pipe(takeUntil(this.destroy$))
        .subscribe((titulo: string) => {
          this.onTituloChange(titulo);
          this.cdr.detectChanges();
        });
    }
    if (component.iconeChange) {
      component.iconeChange
        .pipe(takeUntil(this.destroy$))
        .subscribe((icone: string) => {
          this.onIconeChange(icone);
          this.cdr.detectChanges();
        });
    }

    this.activateDescricao(component.tituloDescricao, component.textoDescricao, component.mostrarDescricao)
  }

  activateDescricao(tituloDescricao: any, textoDescricao: any, mostrarDescricao: any) {
    if (tituloDescricao) {
      tituloDescricao
        .pipe(takeUntil(this.destroy$))
        .subscribe((titulo: string) => {
          this.tituloDescricao = titulo;
          this.cdr.detectChanges();
        });
    }
    if (textoDescricao) {
      textoDescricao
        .pipe(takeUntil(this.destroy$))
        .subscribe((texto: string) => {
          this.textoDescricao = texto;
          this.cdr.detectChanges();
        });
    }
    if (mostrarDescricao) {
      mostrarDescricao
        .pipe(takeUntil(this.destroy$))
        .subscribe((mostrar: boolean) => {
          this.mostrarDescricao = mostrar;
          this.cdr.detectChanges();
        });
    }
  }

  shouldUseFieldset(): boolean {
    return !this.noFieldsetRoutes.some(route => this.router.url.includes(route));
  }

  isIconImage(): boolean {
    return this.icone.startsWith('assets/') || this.icone.startsWith('/assets/');
  }

  showDialog() {
    this.visible = true;
  }
}
